
import { defineComponent, computed, ref, PropType, onMounted, onBeforeUnmount, watch } from 'vue';
import Card from 'primevue/card';
import ProgressSpinner from 'primevue/progressspinner';
import { getBankBgClass, getBankImage } from '../../utils/bank';
import { useToast } from 'primevue/usetoast';
import { formatCurrency } from '../../utils/currency';

type BankInfo = {
	fullName: string;
	bankName: string;
	bankNumber: string;
	bankCode: string;
};

export default defineComponent({
	name: 'BankDetail',
	emits: ['expired', 'tick'],
	methods: { formatCurrency, getBankBgClass, getBankImage },
	components: {
		Card,
		ProgressSpinner,
	},
	props: {
		isActivate: { type: Boolean, default: true },
		isCopyNumber: { type: Boolean, default: false },
		bank: {
			type: Object as PropType<BankInfo>,
			default: () => ({ fullName: '', bankName: '', bankNumber: '', bankCode: '' }),
		},
		showAmount: { type: Number, default: 0 },
		expireTime: { type: Number, default: 180 }, // วินาทีที่พาเรนต์ส่งมา
		loading: { type: Boolean, default: false },
	},
	setup(props, { emit }) {
		const toast = useToast();

		const isLoading = computed(() => props.loading);
		const bank = computed(() => props.bank);

		const isActive = computed(
			() =>
				props.isActivate ||
				(!!bank.value.bankNumber &&
					!!bank.value.bankCode &&
					!!bank.value.bankName &&
					!!bank.value.fullName)
		);

		const copied = ref(false);
		const copyToClipboard = async () => {
			try {
				const raw = bank.value.bankNumber ?? '';
				const digitsOnly = raw.replace(/[^\d]/g, '');
				if (navigator.clipboard?.writeText) {
					await navigator.clipboard.writeText(digitsOnly);
				} else {
					const ta = document.createElement('textarea');
					ta.value = digitsOnly;
					ta.style.position = 'fixed';
					ta.style.left = '-9999px';
					document.body.appendChild(ta);
					ta.select();
					document.execCommand('copy');
					document.body.removeChild(ta);
				}
				copied.value = true;
				setTimeout(() => (copied.value = false), 1000);
				toast?.add?.({ severity: 'success', summary: 'คัดลอกเลขบัญชีสำเร็จ', detail: 'คัดลอกแล้ว', life: 1500 });
			} catch (error: any) {
				toast?.add?.({ severity: 'error', summary: 'เกิดข้อผิดพลาดบางอย่าง', detail: error?.message || String(error), life: 3000 });
			}
		};

		const remaining = ref<number>(Math.max(0, props.expireTime));
		let timer: number | null = null;
		let notified = false;

		const stopCountdown = () => {
			if (timer !== null) {
				window.clearInterval(timer);
				timer = null;
			}
		};

		const tick = () => {
			remaining.value = Math.max(0, remaining.value - 1);
			emit('tick', remaining.value);

			if (remaining.value === 0) {
				stopCountdown();
				if (!notified && props.isActivate) {
					notified = true;
					toast?.add?.({
						severity: 'warn',
						summary: 'หมดเวลา',
						detail: 'เวลาหมดแล้ว กรุณาสร้างรายการใหม่อีกครั้ง',
						life: 3000,
					});
					emit('expired');
				}
			}
		};

		const startCountdown = () => {
			stopCountdown();
			notified = false;
			if (remaining.value > 0) {
				timer = window.setInterval(tick, 1000); // ลดทีละ 1 วิ
				// อัปเดตทันทีรอบแรก เพื่อไม่ต้องรอ 1 วิแรก
				// (ถ้าไม่ต้องการให้เด้งทันที สามารถลบบรรทัดนี้ได้)
				tick();
			} else {
				if (!notified && props.isActivate) {
					notified = true;
					emit('expired');
				}
			}
		};

		onMounted(() => {
			remaining.value = Math.max(0, props.expireTime);
			startCountdown();
		});

		onBeforeUnmount(() => stopCountdown());

		watch(
			() => props.expireTime,
			(v) => {
				remaining.value = Math.max(0, v);
				startCountdown();
			}
		);

		return {
			isLoading,
			isActive,
			bank,
			copied,
			copyToClipboard,
			remaining,
		};
	},
});
